import React from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  FaChartLine,
  FaCheckCircle,
  FaCrown,
  FaExclamationTriangle,
  FaPlug,
  FaUserClock,
  FaUserPlus,
  FaUsers,
} from 'react-icons/fa';
import { PAYMENT_CATALOG } from '../config/paymentCatalog';
import { planLabel } from '../config/adminUsageAnalytics';

const PLAN_ROWS = [
  { key: 'sensor_free', sub: 'Vitalício · 1 estação', color: '#0f9f8f' },
  { key: 'starter', sub: 'Teste de 30 dias', color: '#7e8f9a' },
  { key: 'essential', color: '#0787b8' },
  { key: 'professional', color: '#176dcc' },
  { key: 'management', color: '#07977c' },
];
const REVENUE_PLAN_IDS = ['essential', 'professional', 'management'];
const ATTENTION_LIMIT = 5;
const formatMoney = value => value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const formatDate = value => new Date(value).toLocaleDateString('pt-BR');

function AttentionList({ icon, title, items, renderItem, actionLabel, onAction }) {
  return (
    <div>
      <h3>{icon} {title} ({items.length})</h3>
      {items.slice(0, ATTENTION_LIMIT).map(renderItem)}
      {items.length > ATTENTION_LIMIT && <div className="admin-empty">+ {items.length - ATTENTION_LIMIT} outros</div>}
      <button type="button" className="admin-btn is-ghost" onClick={onAction}>{actionLabel}</button>
    </div>
  );
}

export default function AdminVisaoGeral({
  loading,
  labelPeriodo,
  contas,
  admins,
  ativos,
  retencao,
  receita,
  receitaBreakdown,
  cadastros,
  estatisticasPlanos,
  novosUsuarios,
  planosExpirando,
  addonsExpirando,
  ultimosAcessos,
  versoes,
  versaoAtual,
  onNavigate,
}) {
  const pendencias = novosUsuarios.length + planosExpirando.length + addonsExpirando.length;
  const ativosPct = contas > 0 ? Math.round((ativos / contas) * 100) : 0;
  const totalVersoes = versoes.reduce((sum, item) => sum + item.total, 0);
  const semPlanoAtivo = Object.values(receitaBreakdown.planos).every(item => item.mensal === 0 && item.anual === 0);

  const chart = {
    options: {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
      dataLabels: { enabled: false },
      colors: ['#168673'],
      grid: { borderColor: '#edf2f5' },
      xaxis: { categories: cadastros.labels, labels: { style: { fontSize: '10px' } }, tickAmount: Math.min(cadastros.labels.length, 15) },
      yaxis: { min: 0, forceNiceScale: true, labels: { formatter: value => Math.round(value) } },
      tooltip: { y: { formatter: value => `${Math.round(value)} cadastro(s)` } },
    },
    series: [{ name: 'Novos cadastros', data: cadastros.values }],
  };

  return (
    <main className="admin-panel-content admin-dashboard-content">
      <div className="admin-section-heading">
        <div><span>Saúde da operação</span><h2>Visão geral da plataforma</h2></div>
        <p>Receita, base de clientes e pendências. O comportamento de uso por tela está em &quot;Uso e engajamento&quot;. Administradores não entram nas métricas.</p>
      </div>

      <div className="admin-metrics-grid">
        <div className="admin-metric-card is-users">
          <span className="admin-metric-icon"><FaUsers /></span>
          <div className="admin-metric-value">{contas}</div>
          <div className="admin-metric-label">Contas de clientes</div>
          <div className="admin-metric-note">{admins} administrador(es) fora da contagem</div>
        </div>

        <div className="admin-metric-card is-active-users">
          <span className="admin-metric-icon"><FaUserClock /></span>
          <div className="admin-metric-value">{loading ? '…' : ativos}</div>
          <div className="admin-metric-label">Usuários ativos</div>
          <div className="admin-metric-note">{loading ? labelPeriodo : `${ativosPct}% das contas · ${labelPeriodo}`}</div>
        </div>

        <div className="admin-metric-card is-retention">
          <span className="admin-metric-icon"><FaChartLine /></span>
          <div className="admin-metric-value">{loading ? '…' : `${retencao}%`}</div>
          <div className="admin-metric-label">Retenção</div>
          <div className="admin-metric-note">Ativos que já usavam e voltaram na última semana</div>
        </div>

        <div className="admin-metric-card is-revenue">
          <span className="admin-metric-icon"><FaCrown /></span>
          <div className="admin-metric-value is-currency">R$ {formatMoney(receita)}</div>
          <div className="admin-metric-label">Receita estimada/mês</div>
          <div className="admin-metric-detail">
            {REVENUE_PLAN_IDS.flatMap(planId => ['mensal', 'anual'].map(periodicidade => {
              const quantidade = receitaBreakdown.planos[planId][periodicidade];
              if (!quantidade) return null;
              return <div key={`${planId}-${periodicidade}`}>{quantidade}× {PAYMENT_CATALOG[planId].name} {periodicidade}</div>;
            }))}
            {semPlanoAtivo && <div>Nenhum plano pago ativo</div>}
            {receitaBreakdown.expirados > 0 && <div>{receitaBreakdown.expirados} vencido(s) fora do cálculo</div>}
          </div>
        </div>
      </div>

      <section className={`admin-card ${pendencias > 0 ? 'is-attention' : 'is-clear'}`}>
        <div className="admin-card-head">
          <div>
            <h3 className="admin-card-title">
              {pendencias > 0 ? <FaExclamationTriangle /> : <FaCheckCircle />} Requer atenção
            </h3>
            <p className="admin-card-hint">
              {pendencias > 0 ? 'Cadastros novos (48h) e vencimentos dos próximos 7 dias.' : 'Sem novos cadastros ou vencimentos nos próximos 7 dias.'}
            </p>
          </div>
        </div>
        {pendencias > 0 && (
          <div className="admin-attention">
            {novosUsuarios.length > 0 && (
              <AttentionList
                icon={<FaUserPlus />}
                title="Novos cadastros"
                items={novosUsuarios}
                actionLabel="Ver usuários"
                onAction={() => onNavigate('usuarios')}
                renderItem={u => (
                  <div key={u.id} className="admin-list-row">
                    <div><strong>{u.nome || 'Sem nome'}</strong><small>{u.email}</small></div>
                    <div className="admin-list-meta">{new Date(u.criado_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</div>
                  </div>
                )}
              />
            )}
            {planosExpirando.length > 0 && (
              <AttentionList
                icon={<FaCrown />}
                title="Planos vencendo"
                items={planosExpirando}
                actionLabel="Gerenciar planos"
                onAction={() => onNavigate('planos')}
                renderItem={u => (
                  <div key={u.id} className="admin-list-row">
                    <div><strong>{u.nome || 'Sem nome'}</strong><small>{u.email}</small></div>
                    <div className="admin-list-meta"><span className="admin-pill is-warn">{planLabel(u.plano)}</span><br />{formatDate(u.plano_data_expiracao)}</div>
                  </div>
                )}
              />
            )}
            {addonsExpirando.length > 0 && (
              <AttentionList
                icon={<FaPlug />}
                title="Add-ons vencendo"
                items={addonsExpirando}
                actionLabel="Gerenciar planos"
                onAction={() => onNavigate('planos')}
                renderItem={item => (
                  <div key={item._key} className="admin-list-row">
                    <div><strong>{item.nome || 'Sem nome'}</strong><small>{item.email}</small></div>
                    <div className="admin-list-meta"><span className="admin-pill is-info">{item.addon}</span><br />{formatDate(item.expiracao)}</div>
                  </div>
                )}
              />
            )}
          </div>
        )}
      </section>

      <div className="admin-grid is-wide">
        <section className="admin-card">
          <div className="admin-card-head">
            <div>
              <h3 className="admin-card-title"><FaUserPlus /> Novos cadastros</h3>
              <p className="admin-card-hint">{cadastros.total} no período · {cadastros.porMes ? 'agrupado por mês' : 'agrupado por dia'}</p>
            </div>
          </div>
          <ReactApexChart options={chart.options} series={chart.series} type="bar" height={240} />
        </section>

        <section className="admin-card">
          <div className="admin-card-head">
            <div>
              <h3 className="admin-card-title"><FaCrown /> Contas por plano</h3>
              <p className="admin-card-hint">{estatisticasPlanos.trial} em período de teste</p>
            </div>
            <button type="button" className="admin-btn is-ghost" onClick={() => onNavigate('planos')}>Gerenciar</button>
          </div>
          {PLAN_ROWS.map(row => {
            const value = estatisticasPlanos[row.key] || 0;
            const price = PAYMENT_CATALOG[row.key]?.monthly;
            return (
              <div key={row.key} className="admin-bar-row" style={{ '--bar-color': row.color }}>
                <div>
                  <span><strong>{planLabel(row.key)}</strong> <small>{row.sub || `R$ ${price?.toFixed(2)}/mês`}</small></span>
                  <strong>{value}</strong>
                </div>
                <div className="admin-bar"><span style={{ width: `${contas > 0 ? Math.round((value / contas) * 100) : 0}%` }} /></div>
              </div>
            );
          })}
        </section>
      </div>

      <div className="admin-grid is-wide">
        <section className="admin-card">
          <div className="admin-card-head">
            <div>
              <h3 className="admin-card-title"><FaUserClock /> Últimos acessos</h3>
              <p className="admin-card-hint">Contas de clientes com atividade no período.</p>
            </div>
            <button type="button" className="admin-btn is-ghost" onClick={() => onNavigate('usuarios')}>Ver todos</button>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table" style={{ minWidth: 480 }}>
              <thead>
                <tr>
                  <th className="is-left">Usuário</th>
                  <th>Plano</th>
                  <th>Versão</th>
                  <th>Último acesso</th>
                </tr>
              </thead>
              <tbody>
                {ultimosAcessos.length === 0 && <tr><td colSpan={4} className="admin-empty">Sem acessos no período.</td></tr>}
                {ultimosAcessos.map(item => (
                  <tr key={item.id}>
                    <td className="is-left"><strong>{item.nome || 'Sem nome'}</strong><small>{item.email}</small></td>
                    <td><span className="admin-pill">{planLabel(item.plano)}{item.trial ? ' · teste' : ''}</span></td>
                    <td>{item.versao ? <span className={`admin-pill ${item.versao === versaoAtual ? 'is-ok' : 'is-warn'}`}>v{item.versao}</span> : '-'}</td>
                    <td>{new Date(item.ultimoAcesso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="admin-card">
          <div className="admin-card-head">
            <div>
              <h3 className="admin-card-title"><FaPlug /> Versões do app</h3>
              <p className="admin-card-hint">Versão atual: v{versaoAtual} · {totalVersoes} usuário(s) identificado(s)</p>
            </div>
          </div>
          {versoes.length === 0 && <div className="admin-empty">Nenhuma versão identificada no período.</div>}
          {versoes.map(({ versao, total, atual }) => {
            const pct = totalVersoes > 0 ? Math.round((total / totalVersoes) * 100) : 0;
            return (
              <div key={versao} className="admin-bar-row" style={{ '--bar-color': atual ? '#168673' : '#9fb3c0' }}>
                <div>
                  <span><strong>v{versao}</strong> {atual && <span className="admin-pill is-ok">atual</span>}</span>
                  <span>{total} <small>({pct}%)</small></span>
                </div>
                <div className="admin-bar"><span style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </section>
      </div>
    </main>
  );
}
